import { useEffect, useState } from "react";
import { Cloud, Loader2, Save, Plus, X, ShieldAlert, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const KEYS = ["cloudtrail_alerts_enabled", "cloudtrail_alert_emails", "cloudtrail_alert_user_ids",
  "cloudtrail_alert_regions", "cloudtrail_alert_patterns", "cloudtrail_alert_root"];

function Chip({ label, onRemove, testid }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-xs text-slate-700" data-testid={testid}>
      {label}
      {onRemove && (
        <button type="button" onClick={onRemove} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700">
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

export function CloudTrailAlertsCard({ canEdit, staffOptions = [] }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [emailDraft, setEmailDraft] = useState("");
  const [patternDraft, setPatternDraft] = useState("");

  const load = () => api.get("/aws-cloudtrail/alerts/status").then((r) => {
    setStatus(r.data);
    const c = r.data.config || {};
    setForm({
      enabled: !!c.enabled,
      emails: c.emails || [],
      user_ids: c.user_ids || [],
      regions: c.regions || [],
      patterns: c.using_defaults ? [] : (c.patterns || []),
      root: c.root_activity !== false,
    });
  }).catch(() => setStatus({ error: true }));

  useEffect(() => { load(); }, []);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        cloudtrail_alerts_enabled: form.enabled,
        cloudtrail_alert_emails: form.emails,
        cloudtrail_alert_user_ids: form.user_ids,
        cloudtrail_alert_regions: form.regions,
        cloudtrail_alert_patterns: form.patterns,
        cloudtrail_alert_root: form.root,
      });
      toast.success("CloudTrail alerts saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const run = async (deliver) => {
    setBusy(deliver ? "send" : "preview");
    setPreview(null);
    try {
      const { data } = await api.post(`/aws-cloudtrail/alerts/test?hours=24&deliver=${deliver}`);
      setPreview(data);
      if (deliver) {
        const d = data.delivered;
        toast.success(d ? `Alert sent — ${d.emailed} email(s), ${d.notified_users} staff notified`
          : "Nothing risky in the last 24h, so no alert was sent");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setBusy(""); }
  };

  if (!status || !form) {
    return <Card className="p-6 text-sm text-slate-400" data-testid="cloudtrail-alerts-loading">Loading CloudTrail alerts…</Card>;
  }

  const staffLeft = staffOptions.filter((u) => !form.user_ids.includes(u.user_id));
  const nameFor = (id) => staffOptions.find((u) => u.user_id === id)?.name || id;
  const addEmail = () => {
    const v = emailDraft.trim().toLowerCase();
    if (!v || !v.includes("@")) return toast.error("Enter a valid email address");
    if (form.emails.includes(v)) return setEmailDraft("");
    upd({ emails: [...form.emails, v] });
    setEmailDraft("");
  };
  const addPattern = () => {
    const v = patternDraft.trim();
    if (!v) return;
    if (!form.patterns.includes(v)) upd({ patterns: [...form.patterns, v] });
    setPatternDraft("");
  };

  return (
    <Card className="border-slate-200 shadow-sm p-6 space-y-5 max-w-3xl" data-testid="cloudtrail-alerts-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading flex items-center gap-2 text-base font-semibold text-slate-800">
            <Cloud className="h-4 w-4 text-primary" /> AWS CloudTrail alerts
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Watch the AWS trail for risky actions — deletes, IAM and access-key changes, trail tampering —
            and alert the people below. Checked every 30 minutes; each event alerts once.
          </p>
        </div>
        <Switch checked={form.enabled} disabled={!canEdit || !status.configured}
          onCheckedChange={(v) => upd({ enabled: v })} data-testid="cloudtrail-alerts-enabled" />
      </div>

      {!status.configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="cloudtrail-alerts-unconfigured">
          AWS keys aren&apos;t set in the backend environment yet — alerts stay off until they are.
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email recipients</div>
        <div className="flex flex-wrap gap-1.5" data-testid="cloudtrail-alert-emails">
          {form.emails.length
            ? form.emails.map((e) => <Chip key={e} label={e} testid={`cloudtrail-email-${e}`}
                onRemove={canEdit ? () => upd({ emails: form.emails.filter((x) => x !== e) }) : null} />)
            : <span className="text-xs text-slate-400">No email recipients yet.</span>}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              placeholder="security@yourcompany.com" className="h-9 max-w-xs" data-testid="cloudtrail-email-input" />
            <Button variant="outline" size="sm" onClick={addEmail} data-testid="cloudtrail-email-add">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Staff to notify (bell + their email)</div>
        <div className="flex flex-wrap gap-1.5" data-testid="cloudtrail-alert-staff">
          {form.user_ids.length
            ? form.user_ids.map((id) => <Chip key={id} label={nameFor(id)} testid={`cloudtrail-staff-${id}`}
                onRemove={canEdit ? () => upd({ user_ids: form.user_ids.filter((x) => x !== id) }) : null} />)
            : <span className="text-xs text-slate-400">No staff selected yet.</span>}
        </div>
        {canEdit && (
          <Select value="" onValueChange={(v) => upd({ user_ids: [...form.user_ids, v] })}>
            <SelectTrigger className="h-9 max-w-xs" data-testid="cloudtrail-staff-select">
              <SelectValue placeholder="Add a staff member…" />
            </SelectTrigger>
            <SelectContent>
              {staffLeft.map((u) => (
                <SelectItem key={u.user_id} value={u.user_id} data-testid={`cloudtrail-staff-option-${u.user_id}`}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Regions watched</div>
        <div className="flex flex-wrap gap-1.5" data-testid="cloudtrail-alert-regions">
          {(status.regions_available || []).map((r) => {
            const on = form.regions.includes(r);
            return (
              <button key={r} type="button" disabled={!canEdit} data-testid={`cloudtrail-region-${r}`}
                onClick={() => upd({ regions: on ? form.regions.filter((x) => x !== r) : [...form.regions, r] })}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${on ? "bg-primary text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                {r}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
        <div>
          <div className="text-sm font-medium text-slate-700">Always alert on root-account activity</div>
          <div className="text-xs text-slate-400">Any action taken by the AWS root user, risky or not.</div>
        </div>
        <Switch checked={form.root} disabled={!canEdit} onCheckedChange={(v) => upd({ root: v })}
          data-testid="cloudtrail-alert-root" />
      </div>

      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Risky-action watch list</div>
        <p className="text-xs text-slate-400">
          Event-name patterns (<code className="font-mono">*</code> wildcard). Leave empty to use the
          built-in list of {(status.default_patterns || []).length} patterns.
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="cloudtrail-alert-patterns">
          {(form.patterns.length ? form.patterns : status.default_patterns || []).map((p) => (
            <Chip key={p} label={p} testid={`cloudtrail-pattern-${p}`}
              onRemove={canEdit && form.patterns.length ? () => upd({ patterns: form.patterns.filter((x) => x !== p) }) : null} />
          ))}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Input value={patternDraft} onChange={(e) => setPatternDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPattern(); } }}
              placeholder="Delete*" className="h-9 max-w-xs font-mono text-xs" data-testid="cloudtrail-pattern-input" />
            <Button variant="outline" size="sm" onClick={addPattern} data-testid="cloudtrail-pattern-add">
              <Plus className="h-4 w-4" /> Add pattern
            </Button>
            {!!form.patterns.length && (
              <Button variant="ghost" size="sm" onClick={() => upd({ patterns: [] })} data-testid="cloudtrail-pattern-reset">
                Use built-in list
              </Button>
            )}
          </div>
        )}
      </div>

      {status.last_run && (
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="cloudtrail-alerts-last-run">
          Last check {new Date(status.last_run).toLocaleString()} · {status.last_result?.new_events ?? 0} new event(s) ·
          {" "}{(status.recent_alerts || []).length} alert(s) on record
        </div>
      )}

      {preview && (
        <div className="rounded-lg border border-slate-200" data-testid="cloudtrail-alerts-preview">
          <div className="border-b border-slate-100 px-3 py-2 text-xs font-medium text-slate-600">
            {preview.matched} risky action{preview.matched === 1 ? "" : "s"} in the last 24h · {preview.regions.join(", ")}
          </div>
          <ul className="max-h-56 divide-y divide-slate-50 overflow-auto text-xs">
            {(preview.sample || []).map((s) => (
              <li key={s.event_id} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                <span className="font-medium text-slate-700">{s.event_name}</span>
                <span className="text-slate-500">{s.user}</span>
                <span className="text-slate-400">{s.service} · {s.region}</span>
                <span className="ml-auto text-slate-400">{s.time ? new Date(s.time).toLocaleString() : ""}</span>
              </li>
            ))}
            {!preview.matched && <li className="px-3 py-3 text-slate-400">Nothing matched — the trail is quiet.</li>}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
        <Button variant="outline" size="sm" disabled={!status.configured || !!busy}
          onClick={() => run(false)} data-testid="cloudtrail-alerts-preview-btn">
          {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview last 24h
        </Button>
        <Button variant="outline" size="sm" disabled={!status.configured || !!busy}
          onClick={() => run(true)} data-testid="cloudtrail-alerts-send-btn">
          {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test alert
        </Button>
        {canEdit && (
          <Button size="sm" onClick={save} disabled={saving} data-testid="cloudtrail-alerts-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </Button>
        )}
      </div>
    </Card>
  );
}

export default CloudTrailAlertsCard;
